import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    GECKO_CONSTS,
    GeckoBtn,
    GeckoBtns,
    GeckoCard,
    GeckoCardTable,
    GeckoDialogService,
    GeckoFields,
    GeckoLoadingService,
    GeckoModalService,
    GeckoToastService,
} from '@geckolabs/gecko-react-ui';
import AccountLogic from '../../../../Logic/Account';
import ToggleDomainSso from './ToggleDomainSso';

const DEFAULT_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [5, 10, 15];

const ConfirmMessage = ({ email, provider, isSsoLinked }) => (
    <>
        <p>
            {isSsoLinked ? (
                <>Are you sure you want to <strong style={{ fontWeight: 700 }}>disable SSO</strong> for <strong style={{ fontWeight: 700 }}>{email}</strong>?</>
            ) : (
                <>Are you sure you want to <strong style={{ fontWeight: 700 }}>enable SSO</strong> for <strong style={{ fontWeight: 700 }}>{email}</strong>{provider ? <> via <strong style={{ fontWeight: 700 }}>{provider}</strong></> : null}?</>
            )}
        </p>
    </>
);

const filterUsers = (users = [], searchTerm = '') => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
        return users;
    }

    return users.filter((user) => {
        const name = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();

        return name.includes(query)
            || (user.email || '').toLowerCase().includes(query)
            || (user.provider || '').toLowerCase().includes(query);
    });
};

export default function SsoUsers({ accountUuid }) {
    const [users, setUsers] = useState([]);
    const [idps, setIdps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const identityProviderOptions = useMemo(
        () => AccountLogic.getIdentityProviderOptions(idps),
        [idps]
    );

    const ssoEligibleUsers = useMemo(
        () => AccountLogic.filterSsoEligibleUsers(users, idps),
        [users, idps]
    );

    const filteredUsers = useMemo(() => filterUsers(ssoEligibleUsers, search), [ssoEligibleUsers, search]);

    const loadIdps = useCallback(() => {
        return AccountLogic.getIdps()
            .then((portalIdps) => {
                setIdps(portalIdps);
                return portalIdps;
            })
            .catch((err) => {
                setIdps([]);
                new GeckoToastService().error(err?.message || 'Failed to load identity providers.');
                return [];
            });
    }, []);

    const loadAccountUsers = useCallback(() => {
        if (!accountUuid) {
            setUsers([]);
            return Promise.resolve([]);
        }

        return AccountLogic.getAccountUsers(accountUuid)
            .then((portalUsers) => {
                setUsers(portalUsers);
                return portalUsers;
            })
            .catch((err) => {
                setUsers([]);
                new GeckoToastService().error(err?.message || 'Failed to load SSO users.');
                return [];
            });
    }, [accountUuid]);

    const loadData = useCallback(() => {
        if (!accountUuid) {
            setUsers([]);
            setIdps([]);
            setLoading(false);
            return Promise.resolve();
        }

        setLoading(true);

        return Promise.all([
            loadAccountUsers(),
            loadIdps(),
        ]).finally(() => {
            setLoading(false);
        });
    }, [accountUuid, loadAccountUsers, loadIdps]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const handleSearchChange = (event) => {
        setSearch(event.target.value);
        setPage(1);
    };

    const paginationBtns = [
        {
            icon: 'angle-left',
            ariaLabel: 'Previous',
            handler: () => setPage(currentPage - 1),
            disabledWhen: () => currentPage === 1,
        },
        {
            icon: 'angle-right',
            ariaLabel: 'Next',
            handler: () => setPage(currentPage + 1),
            disabledWhen: () => currentPage === totalPages,
        },
    ];

    const handlePageSizeChange = (key, value) => {
        setPageSize(Number(value));
        setPage(1);
    };

    const openToggleDomainModal = () => {
        if (!identityProviderOptions.length) {
            new GeckoToastService().error('No SSO identity providers are configured.');
            return;
        }

        new GeckoModalService().open(() => (
            <ToggleDomainSso
                identityProviderOptions={identityProviderOptions}
                onSubmit={(identityProviderId, action, selectedOption) => {
                    new GeckoLoadingService().show();

                    return AccountLogic.toggleIdentityProviderSsoForUsers(identityProviderId, action)
                        .then(() => {
                            new GeckoModalService().close();
                            new GeckoToastService().success(
                                selectedOption?.domain
                                    ? `SSO ${action === 'off' ? 'disabled' : 'enabled'} for all users on @${selectedOption.domain}.`
                                    : `SSO ${action === 'off' ? 'disabled' : 'enabled'} for all users on the selected identity provider.`
                            );
                            return loadData();
                        })
                        .catch((err) => {
                            new GeckoToastService().error(err?.message || 'Failed to update SSO for identity provider.');
                        })
                        .finally(() => {
                            new GeckoLoadingService().hide();
                        });
                }}
            />
        ));
    };

    const handleToggle = useCallback((row, newValue) => {
        return new GeckoDialogService().confirm(
            <ConfirmMessage email={row.email} provider={row.provider} isSsoLinked={!newValue} />
        ).then((ok) => {
            if (!ok) {
                return;
            }

            new GeckoLoadingService().show();

            return AccountLogic.toggleSsoUser(accountUuid, row.id)
                .then((updatedUser) => {
                    setUsers((currentUsers) => currentUsers.map((user) => (
                        user.id === updatedUser.id ? updatedUser : user
                    )));
                    new GeckoToastService().success(
                        `SSO ${updatedUser.ssoEnabled ? 'enabled' : 'disabled'} for ${updatedUser.email}.`
                    );
                })
                .catch((err) => {
                    new GeckoToastService().error(err?.message || 'Failed to update SSO status.');
                    return loadData();
                })
                .finally(() => {
                    new GeckoLoadingService().hide();
                });
        });
    }, [accountUuid, loadData]);

    const cols = useMemo(() => [
        {
            key: 'name',
            name: 'Name',
            render: (value, row) => (
                <span>{`${row.firstName || ''} ${row.lastName || ''}`.trim() || '—'}</span>
            ),
        },
        {
            key: 'email',
            name: 'Email',
            render: (value) => (<span>{value}</span>),
        },
        {
            key: 'provider',
            name: 'Auth Provider',
            render: (value) => (<span>{value || '—'}</span>),
        },
        {
            key: 'ssoEnabled',
            name: 'SSO Enabled',
            render: (value, row) => (
                <div className="liteToggleWrapper d-flex justify-content-end">
                    <GeckoFields
                        fields={[{
                            key: 'ssoEnabled',
                            label: '',
                            type: GECKO_CONSTS.FIELD_TYPE_TOGGLE,
                        }]}
                        values={{ ssoEnabled: value }}
                        handler={(key, newValue) => handleToggle(row, newValue)}
                    />
                </div>
            ),
        },
    ], [handleToggle]);

    const renderTableFooter = () => (
        <div className="gecko-ui-card-footer card-footer p-3 border-top ssoUsersTableFooter">
            <div className="gecko-ui-pagination ssoUsersTablePagination">
                <span className="results-text">
                    Found {filteredUsers.length} results.
                    {filteredUsers.length > 0 && (
                        <> Showing page {currentPage} of {totalPages}.</>
                    )}
                </span>
                <div className="ssoUsersTablePaginationControls">
                    <GeckoFields
                        fields={[{
                            key: 'pageSize',
                            label: 'Per page',
                            type: GECKO_CONSTS.FIELD_TYPE_SELECT,
                            width: 'auto',
                            optionValueKey: 'value',
                            optionsLabelKey: 'label',
                            options: PAGE_SIZE_OPTIONS.map((size) => ({
                                value: size,
                                label: String(size),
                            })),
                        }]}
                        values={{ pageSize }}
                        handler={handlePageSizeChange}
                    />
                    {totalPages > 1 && (
                        <GeckoBtns items={paginationBtns} group />
                    )}
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <GeckoCard>
                <div className="p-3">Loading SSO users...</div>
            </GeckoCard>
        );
    }

    return (
        <GeckoCard>
            <div className="p-3 pb-0 d-flex justify-content-end">
                <GeckoBtn
                    name="Toggle SSO for domain"
                    className="btn-default"
                    handler={openToggleDomainModal}
                />
            </div>

            {!idps.length && (
                <div className="px-3 pt-3">
                    <p className="mb-0 text-muted">No SSO identity providers are configured.</p>
                </div>
            )}

            {!ssoEligibleUsers.length ? (
                <div className="p-3 pt-0">
                    <p className="mb-0 text-muted">No users with SSO-eligible email domains found for this account.</p>
                </div>
            ) : (
                <>
                    <div className="px-3 pb-3">
                        <input
                            className="moduleSearch"
                            placeholder="Search by name, email, or provider..."
                            value={search}
                            onChange={handleSearchChange}
                        />
                    </div>
                    {!filteredUsers.length ? (
                        <div className="p-3 pt-0">
                            <p className="mb-0 text-muted">No users match your search.</p>
                        </div>
                    ) : (
                        <div className="ssoUsersTable">
                            <GeckoCardTable
                                name="SSO Users"
                                cols={cols}
                                rows={paginatedUsers}
                                pagination={false}
                            />
                            {renderTableFooter()}
                        </div>
                    )}
                </>
            )}
        </GeckoCard>
    );
}
